import { defineStore } from 'pinia';
import { proxyUrl, apiUrl, username, password } from '/config';
import { mapLocale } from '@/utils/localeMapper';
import { useAppStore } from '@/stores/app';
import { useAnalyticsStore } from '@/stores/analytics';

export const useSettingsStore = defineStore('settings', {
  state: () => ({
    settings: {
      proxyUrl,
      apiUrl,
      logoUrl: '',
      projectCode: null,
      projectName: null,
      scopedToken: null,
      username,
      password,
      selectedProject: null,
      userId: null,
      defaultTeamId: null,
      locale: 'defaultLocale',
      timeZone: 'UTC',
      showOpenItemsOnly: false,
    },
  }),
  getters: {
    getSettings: (state) => state.settings,
    getShowOpenItemsOnly: (state) => state.settings.showOpenItemsOnly,
    getApiUrl: (state) => state.settings.apiUrl,
    getLogoUrl: (state) => state.settings.logoUrl,
    getProjectCode: (state) => state.settings.projectCode,
    getProjectName: (state) => state.settings.projectName,
    getScopedToken: (state) => state.settings.scopedToken,
    getUrl: (state) => state.settings.apiUrl,
    getUsername: (state) => state.settings.username,
    getPassword: (state) => state.settings.password,
    getSelectedProject: (state) => state.settings.projectCode,
    currentLocale: (state) => state.settings.locale,
  },
  actions: {
    setSettings(newSettings) {
      this.settings = { ...this.settings, ...newSettings };
    },
    updateSettingsField({ field, value }) {
      this.settings[field] = value;
    },
    setApiToken(token) {
      const appStore = useAppStore();
      appStore.apiClient.setToken(token);
      this.settings.scopedToken = token;
    },
    setUserId(value) {
      this.settings.userId = value;
    },
    setDefaultTeamId(value) {
      this.settings.defaultTeamId = value;
    },
    setProjectCode(value) {
      const appStore = useAppStore();
      appStore.apiClient.setProjectCode(value);
      this.settings.projectCode = value;
    },
    setProjectName(value) {
      this.settings.projectName = value;
    },
    setScopedToken(value) {
      this.settings.scopedToken = value;
    },
    setValues(values) {
      const appStore = useAppStore();
      this.settings.apiUrl = values.apiUrl;
      this.settings.projectCode = values.projectCode;
      this.settings.projectName = values.projectName;
      this.settings.scopedToken = values.scopedToken;
      this.settings.locale = values.locale;
      appStore.theme = values.theme;

      appStore.apiClient.setApiUrl(values.apiUrl);
      appStore.apiClient.setProjectCode(values.projectCode);
      appStore.apiClient.setScopedToken(values.scopedToken);
    },
    setLocale(locale) {
      this.settings.locale = locale;
    },
    setLogoUrl(logoUrl) {
      this.settings.logoUrl = logoUrl;
    },
    setTimeZone(timeZone) {
      this.settings.timeZone = timeZone;
    },
    saveSettings(settings) {
      this.setSettings(settings);
    },
    updateProjectCode(projectCode) {
      this.setProjectCode(projectCode);
    },
    updateApiProjectName(projectName) {
      this.setProjectName(projectName);
    },
    updateUserId(userId) {
      this.setUserId(userId);
    },
    updateValues(values) {
      this.setValues(values);
      const analyticsStore = useAnalyticsStore();
      analyticsStore.initializeAnalytics();
    },
    updateLocale(locale) {
      const mappedLocale = mapLocale(locale);
      this.setLocale(mappedLocale);
    },
    updateLogoUrl(logoUrl) {
      this.setLogoUrl(logoUrl);
    },
    updateTimeZone(timeZone) {
      this.setTimeZone(timeZone);
    },
  },
});
